import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axiosInstance from '../api/api';
import { setPageMeta } from '../utils/seo';
import {
  MapPin, PaperPlaneTilt, FilePdf, Dog, Coins, Camera, Microphone, Target,
  Calculator, UsersThree, FileText, ShieldCheck, CalendarCheck, Translate,
  Bell, HardHat, Storefront, RocketLaunch, Sparkle, ArrowRight, ClockCounterClockwise,
  EnvelopeSimple, CalendarPlus, SealCheck, GlobeHemisphereWest, Wallet, ShoppingCart,
  Crown, Gift, CheckSquare, ListChecks, LockKey, Scales, Timer, DeviceMobile,
  Star, IdentificationCard, Warning, ArrowsClockwise, Cube
} from '@phosphor-icons/react';

const ICONS = {
  MapPin, PaperPlaneTilt, FilePdf, Dog, Coins, Camera, Microphone, Target,
  Calculator, UsersThree, FileText, ShieldCheck, CalendarCheck, Translate,
  Bell, HardHat, Storefront, RocketLaunch,
  EnvelopeSimple, CalendarPlus, SealCheck, GlobeHemisphereWest, Wallet, ShoppingCart,
  Crown, Gift, CheckSquare, ListChecks, LockKey, Scales, Timer, DeviceMobile,
  Star, IdentificationCard, Warning, ArrowsClockwise, Cube,
};

const monthLabel = (iso) => {
  const d = new Date(iso + 'T12:00:00Z');
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

export default function FeatureTimeline() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setPageMeta({
      title: "What's New — Feature Timeline | ConstructHelp",
      description: 'Every feature we have shipped for contractors, workers, and homeowners — AI pricing, blueprint analysis, escrow payments, voice translation and more.',
      url: window.location.href,
    });
    (async () => {
      try {
        const resp = await axiosInstance.get('/feature-timeline');
        if (resp.data?.success) setEntries(resp.data.entries || []);
      } catch (e) {
        console.error('Timeline fetch failed:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Group entries by month (already sorted desc by API)
  const groups = [];
  entries.forEach((e) => {
    const label = monthLabel(e.shipped_at);
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.items.push(e);
    else groups.push({ label, items: [e] });
  });

  return (
    <div className="min-h-screen bg-[#F0F0F2] dark:bg-neutral-900" data-testid="feature-timeline-page">
      {/* Hero */}
      <div className="bg-[#0F172A] border-b-4 border-[#D95800]">
        <div className="max-w-4xl mx-auto px-4 py-14 text-center">
          <div className="inline-flex items-center gap-2 bg-[#D95800] text-black text-[10px] font-black uppercase tracking-widest px-3 py-1 border-2 border-white shadow-[3px_3px_0px_#fff] mb-5">
            <ClockCounterClockwise size={14} weight="fill" /> Feature Timeline
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-white leading-tight mb-4">
            Always <span className="text-[#D95800]">building.</span>
          </h1>
          <p className="text-neutral-400 max-w-xl mx-auto text-base">
            Every feature we've shipped for contractors, workers, and homeowners — newest first.
          </p>
        </div>
      </div>

      {/* Timeline */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        {loading && (
          <div className="space-y-6">
            {[0, 1, 2].map((i) => (
              <div key={i} className="bg-white dark:bg-[#0F172A] border-2 border-neutral-300 dark:border-neutral-700 h-36 animate-pulse" />
            ))}
          </div>
        )}

        {!loading && entries.length === 0 && (
          <p className="text-center text-neutral-500 py-20">No updates published yet. Check back soon!</p>
        )}

        {!loading && groups.map((group) => (
          <div key={group.label} className="relative">
            {/* Month header */}
            <div className="sticky top-16 z-10 flex items-center gap-3 py-3 bg-[#F0F0F2]/95 dark:bg-neutral-900/95 backdrop-blur-sm">
              <div className="bg-[#2B4C6F] text-white font-black uppercase tracking-wider text-sm px-4 py-1.5 border-2 border-black shadow-[3px_3px_0px_#000]" data-testid={`timeline-month-${group.label.replace(/\s/g, '-').toLowerCase()}`}>
                {group.label}
              </div>
              <div className="flex-1 h-0.5 bg-neutral-300 dark:bg-neutral-700" />
            </div>

            {/* Entries */}
            <div className="relative ml-4 sm:ml-6 border-l-2 border-black dark:border-neutral-500 pl-6 sm:pl-10 pb-10 space-y-8 pt-4">
              {group.items.map((entry) => {
                const Icon = ICONS[entry.icon] || Sparkle;
                return (
                  <div key={entry.slug} className="relative" data-testid={`timeline-entry-${entry.slug}`}>
                    {/* Timeline dot */}
                    <div
                      className="absolute -left-[37px] sm:-left-[53px] top-5 w-5 h-5 border-2 border-black dark:border-white"
                      style={{ backgroundColor: entry.accent || '#2B4C6F' }}
                    />
                    <div className="bg-white dark:bg-[#0F172A] border-2 border-black dark:border-white shadow-[5px_5px_0px_#000] dark:shadow-[5px_5px_0px_#2B4C6F] hover:translate-x-0.5 hover:-translate-y-0.5 transition-transform">
                      <div className="p-5 sm:p-6">
                        <div className="flex items-start gap-4">
                          <div
                            className="w-12 h-12 border-2 border-black flex items-center justify-center shrink-0"
                            style={{ backgroundColor: entry.accent || '#2B4C6F' }}
                          >
                            <Icon size={24} weight="bold" className="text-white" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <h2 className="text-base md:text-lg font-black uppercase tracking-tight text-neutral-900 dark:text-white">
                                {entry.title}
                              </h2>
                              <span
                                className="text-[10px] font-black uppercase tracking-widest text-white px-2 py-0.5 border border-black"
                                style={{ backgroundColor: entry.accent || '#2B4C6F' }}
                              >
                                {entry.category}
                              </span>
                            </div>
                            <p className="text-sm text-neutral-600 dark:text-neutral-300 mb-3">{entry.description}</p>
                            {entry.highlights?.length > 0 && (
                              <div className="flex flex-wrap gap-2 mb-4">
                                {entry.highlights.map((h, i) => (
                                  <span key={i} className="text-xs font-mono bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 border border-neutral-300 dark:border-neutral-600 px-2 py-1">
                                    {h}
                                  </span>
                                ))}
                              </div>
                            )}
                            {entry.link && (
                              <Link
                                to={entry.link}
                                className="inline-flex items-center gap-1.5 text-sm font-black uppercase tracking-wider text-[#D95800] hover:text-[#B84A00] transition-colors"
                                data-testid={`timeline-cta-${entry.slug}`}
                              >
                                {entry.cta || 'Try it'} <ArrowRight size={16} weight="bold" />
                              </Link>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Bottom CTA */}
        {!loading && entries.length > 0 && (
          <div className="bg-[#2B4C6F] border-2 border-black shadow-[5px_5px_0px_#000] p-8 text-center mt-4">
            <h3 className="text-xl font-black uppercase tracking-tight text-white mb-2">More shipping every week</h3>
            <p className="text-white/70 text-sm mb-5">Join free and get every new tool the moment it lands.</p>
            <Link
              to="/signup"
              className="inline-flex items-center gap-2 bg-[#D95800] hover:bg-[#B84A00] text-white font-black uppercase tracking-wider text-sm px-6 py-3 border-2 border-black shadow-[3px_3px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all"
              data-testid="timeline-signup-cta"
            >
              Get Started Free <ArrowRight size={18} weight="bold" />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
